/**
 * The composer's auto mode state — ONE implementation for every composer that
 * shows the chip (ChatPanel, HeroChat: the home page and mobile).
 *
 * features/agent/core/auto-mode.ts decides what auto mode skips; this only shows
 * and sets it. A chat not created yet keeps its choice per draft key until the
 * create carries it: the create sends `draftValue` (what the chip shows — the
 * draft's own choice, else the default), a carry onto a chat that already
 * existed sets only `draftChoice` (the draft's own switch), then `clearDraft`.
 * A draft nobody switched shows the member's default for new chats (0357, one
 * copy per tab — hooks.ts); the popover sets it too, and re-reads it on opening.
 *
 * A chat on screen that the loaded list leaves out (an archived one reopened from "All chats") is looked up on its
 * own; until it is read, its setting and its "Always" answers are UNKNOWN (`on: null`, `grants: null`) — never shown
 * as off, which is the unsafe guess: the worker may well run it in auto mode.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import i18n from '@papan/i18n'
import { isGrantableRule } from '../core/always-grant.js'
import type { AlwaysGrant } from './AutoModeButton.js'

/** What this reads of a listed session (hooks.ts `AgentSessionInfo`). */
interface ListedSession {
  id: string
  autoMode?: boolean
  permission?: Array<{ permission: string; pattern: string; action: string }>
  wallet?: { canSend?: boolean }
}

interface Options {
  /** The chat the composer sends to, or null while it is a draft. */
  chatId: string | null
  /** The draft's key while `chatId` is null. */
  draftKey: string
  sessions: readonly ListedSession[]
  setSessionAutoMode: (id: string, autoMode: boolean | null, opts?: { reloadList?: boolean }) => Promise<unknown>
  removeAlwaysGrant: (id: string, grant: AlwaysGrant, opts?: { reloadList?: boolean }) => Promise<unknown>
  /** One conversation, archived ones included (hooks.ts). */
  fetchSession: (id: string) => Promise<ListedSession | null>
  /** `sessions` has loaded — before that a chat missing from it says nothing. */
  listLoaded: boolean
  /** The member's own default for new chats in this org (hooks.ts), null = not read. A draft nobody switched shows
   *  it, and the create SENDS what was shown (`draftValue`) — never left to a read the person didn't see. */
  autoModeDefault: boolean | null
  setAutoModeDefault: (on: boolean) => Promise<unknown>
  /** Read the default again (the popover opening). */
  reloadAutoModeDefault: () => void
  toast: { error: (message: string) => void }
}

export function useConversationAutoMode({ chatId, draftKey, sessions, setSessionAutoMode, removeAlwaysGrant, fetchSession, listLoaded, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, toast }: Options) {
  const [drafts, setDrafts] = useState<Record<string, boolean>>({})
  // read by a create that runs before React re-renders (a paste racing the first send)
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const defaultRef = useRef(autoModeDefault)
  defaultRef.current = autoModeDefault

  const listed = chatId ? sessions.find(s => s.id === chatId) : undefined
  // a chat the list leaves out, read on its own; `nonce` re-reads it after this view changed it
  const [lookedUp, setLookedUp] = useState<{ id: string; nonce: number; info: ListedSession | null } | null>(null)
  const [nonce, setNonce] = useState(0)
  const needsLookup = !!chatId && !listed && listLoaded
  useEffect(() => {
    if (!needsLookup || !chatId) return
    if (lookedUp?.id === chatId && lookedUp.nonce === nonce) return
    let live = true
    fetchSession(chatId).then(
      (info) => { if (live) setLookedUp({ id: chatId, nonce, info }) },
      () => { if (live) setLookedUp({ id: chatId, nonce, info: null }) },
    )
    return () => { live = false }
  }, [needsLookup, chatId, nonce, lookedUp, fetchSession])
  // a lookup counts only for THIS chat at the CURRENT read — while a re-read after a save is out, the setting is
  // unknown (never the value from before the save), and an old lookup is never reused
  const info = listed ?? (chatId && lookedUp?.id === chatId && lookedUp.nonce === nonce ? lookedUp.info ?? undefined : undefined)

  const on: boolean | null = chatId ? (info ? info.autoMode === true : null) : (drafts[draftKey] ?? autoModeDefault)
  // the same test the worker reads them with: a rule an earlier build stored that is no longer applied is not shown
  const grants = chatId && !info
    ? null
    : (info?.permission ?? []).filter(r => r.action === 'allow' && isGrantableRule({ ...r, action: 'allow' }))
  const reread = useCallback(() => { if (!listed) setNonce(n => n + 1) }, [listed])

  const onToggle = useCallback(async (next: boolean) => {
    if (!chatId) {
      setDrafts(prev => {
        const updated = { ...prev, [draftKey]: next }
        draftsRef.current = updated
        return updated
      })
      return
    }
    // an unlisted chat is re-read on its own, never by reloading the list (the composer would drop its tab)
    try { await setSessionAutoMode(chatId, next, { reloadList: !!listed }) } catch { toast.error(i18n.t('agent:autoMode.saveFailed')) }
    reread()
  }, [chatId, draftKey, setSessionAutoMode, toast, reread, listed])

  const onRevoke = useCallback(async (grant: AlwaysGrant) => {
    if (!chatId) return
    try { await removeAlwaysGrant(chatId, grant, { reloadList: !!listed }) } catch { toast.error(i18n.t('agent:autoMode.revokeFailed')) }
    reread()
  }, [chatId, removeAlwaysGrant, toast, reread, listed])

  const onToggleDefault = useCallback(async (next: boolean) => {
    try { await setAutoModeDefault(next) } catch { toast.error(i18n.t('agent:autoMode.defaultSaveFailed')) }
  }, [setAutoModeDefault, toast])

  // what the draft's chip shows: its own choice, else the default as shown. Undefined only while the default is unknown
  // (the chip says "?" and its switch is disabled) — the server then starts the chat from the default it reads.
  const draftValue = useCallback((key: string): boolean | undefined => draftsRef.current[key] ?? defaultRef.current ?? undefined, [])
  // ONLY the draft's own switch — what a carry onto a chat that ALREADY existed may set (ChatPanel). Never the default:
  // a carry runs after another caller created the chat from this draft and cleared its choice, and falling back to the
  // default there would overwrite the choice that create carried (scoped review, round 1).
  const draftChoice = useCallback((key: string): boolean | undefined => draftsRef.current[key], [])
  const clearDraft = useCallback((key: string) => {
    setDrafts(prev => {
      if (!(key in prev)) return prev
      const { [key]: _, ...rest } = prev
      draftsRef.current = rest
      return rest
    })
  }, [])

  return {
    chip: {
      on,
      grants,
      onToggle,
      onRevoke,
      defaultOn: autoModeDefault,
      onToggleDefault,
      onRefreshDefault: reloadAutoModeDefault,
      // not read yet: its setting is unknown, so the switch would send a guess (a draft whose default is unread too) —
      // and a chat the viewer can't send to has nothing to switch
      switchDisabled: chatId ? (!info || info.wallet?.canSend === false) : on === null,
    },
    isDraft: !chatId,
    /** The chat is in the loaded list — fresh state comes from reloading the list; otherwise from `reread`. Never
     *  reload the list for an unlisted chat: the composer drops an open tab the list no longer has (an archived chat
     *  would vanish from under the person who only opened the popover). */
    isListed: !!listed,
    /** Read an unlisted chat again. */
    reread,
    draftValue,
    draftChoice,
    clearDraft,
  }
}
